import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Bot } from "lucide-react";
import api, { API, trackActivity } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = [
  "What should I pack for the UK?",
  "How do I open a UK bank account?",
  "How many hours can I work on a student visa?",
  "How can I save money as a student?",
];

export default function CastlesAI() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    trackActivity("page_visit", "Opened Castles AI", "castles-ai");
    api.get("/ai/history").then((res) => {
      if (res.data.length) setMessages(res.data);
      else setMessages([{ role: "assistant", content: `Hi ${user?.first_name || "there"}! I'm your Castles advisor - think of me as a senior student who's done all of this before. Ask me anything about your UK move.` }]);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("cc_token") || ""}` },
        credentials: "include",
        body: JSON.stringify({ message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble connecting right now - please try again in a moment." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="h-screen bg-[#060D1F] flex flex-col" data-testid="castles-ai-page">
      <header className="glass">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center gap-4">
          <button data-testid="back-to-portal" onClick={() => navigate("/portal")} className="text-slate-400 transition-colors duration-300 hover:text-[#D4AF37]">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-9 h-9 rounded-xl bg-[#D4AF37] flex items-center justify-center">
            <Bot className="w-5 h-5 text-[#0A1633]" strokeWidth={2} />
          </div>
          <div>
            <p className="text-white font-medium leading-tight">Castles AI</p>
            <p className="text-xs text-slate-500">Your personal UK student advisor</p>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-8">
        <div className="max-w-3xl mx-auto space-y-6" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-3xl px-6 py-4 leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#D4AF37] text-[#0A1633] rounded-br-lg" : "bg-white/[0.05] border border-white/10 text-slate-200 rounded-bl-lg"
              }`}>
                {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block w-2 h-4 bg-[#D4AF37] animate-pulse rounded" /> : "")}
              </div>
            </div>
          ))}
          {messages.length <= 1 && (
            <div className="grid sm:grid-cols-2 gap-3 pt-4">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid={`suggestion-${s.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => send(s)}
                  className="text-left text-sm rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 text-slate-300 transition-all duration-300 hover:border-[#D4AF37]/40 hover:-translate-y-0.5">
                  {s}
                </button>
              ))}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </main>

      <footer className="border-t border-white/10 bg-[#0A1633]/80 backdrop-blur-xl px-5 py-4">
        <form
          className="max-w-3xl mx-auto flex items-center gap-3"
          onSubmit={(e) => { e.preventDefault(); send(); }}
          data-testid="chat-form"
        >
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything about your UK journey..."
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-6 py-3.5 text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]/70"
          />
          <button
            data-testid="chat-send-button"
            type="submit"
            disabled={streaming || !input.trim()}
            className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center transition-all duration-300 hover:bg-[#e5c558] disabled:opacity-40"
          >
            <Send className="w-5 h-5 text-[#0A1633]" />
          </button>
        </form>
      </footer>
    </div>
  );
}
